import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { navigate } from '../lib/router';
import { LogoSvg } from './LogoSvg';

/**
 * Authenticated app shell: sticky header (logo + nav + user chip + theme +
 * logout) and a `<main>` wrapper. Pages render their own content inside.
 */
export function Layout({
  active = '',
  children,
  maxWidth = 'max-w-7xl',
}: {
  active?: 'sites' | 'admin' | 'affiliate' | 'account' | '';
  children?: any;
  maxWidth?: string;
}) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();

  const linkCls = (key: 'sites' | 'admin' | 'affiliate' | 'account', hover: string) => {
    const isActive = active === key;
    return (
      'text-sm px-3 py-1.5 rounded-md transition-colors ' +
      (isActive ? 'text-cyan-400 bg-dark-700' : `text-slate-400 hover:${hover} hover:bg-dark-700`)
    );
  };

  return (
    <div>
      <header class="bg-[rgba(10,14,20,0.95)] backdrop-blur-[12px] border-b border-slate-700/30 sticky top-0 z-50">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex items-center justify-between h-16">
            <a
              href="/sites"
              onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites'); }}
              class="flex items-center"
              aria-label="WebForCheap home"
            >
              <LogoSvg />
            </a>
            <nav class="flex items-center gap-2 sm:gap-3">
              {user?.isAffiliate && (
                <a
                  href="/affiliate"
                  onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/affiliate'); }}
                  class={linkCls('affiliate', 'text-purple-400')}
                >
                  Affiliate
                </a>
              )}
              {user?.isAdmin && (
                <a
                  href="/admin"
                  onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/admin'); }}
                  class={linkCls('admin', 'text-cyan-400')}
                >
                  Admin
                </a>
              )}
              <a
                href="/account"
                onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/account'); }}
                title="Account settings"
                class={
                  'flex items-center gap-3 pl-1 sm:pl-2 border-l border-slate-700/40 ml-1 rounded-md py-1 pr-2 transition-colors ' +
                  (active === 'account' ? 'bg-dark-700' : 'hover:bg-dark-700')
                }
              >
                <div class="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-dark-900 text-sm font-medium overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt="" class="w-full h-full object-cover" />
                  ) : (
                    (user?.name || user?.email || '?').charAt(0).toUpperCase()
                  )}
                </div>
                <span class={'text-sm font-medium hidden sm:block ' + (active === 'account' ? 'text-cyan-400' : 'text-slate-300')}>
                  {user?.name || user?.email || 'Loading…'}
                </span>
              </a>
              <button
                type="button"
                onClick={toggle}
                class="p-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 rounded-md transition-colors"
                title="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                ) : (
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>
                )}
              </button>
              <button
                type="button"
                onClick={async () => { await logout(); navigate('/'); }}
                class="text-sm text-slate-400 hover:text-slate-200 px-3 py-1.5 rounded-md hover:bg-dark-700 transition-colors"
              >
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>
      <main class={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-8`}>{children}</main>
    </div>
  );
}
